/** GxSupervisoryRating (own bank only): the composite ring and six component pips. Rivals never get a number (screens §1.4). */
import type { RoundReport } from "@bsim/contracts";
import { GxPill } from "../theme/GxPill";
import { usePreviewContext } from "../shell/PreviewProvider";
import { RATING_WORDS, SUPERVISORY_WORDS, supervisoryTiming } from "./derive";
import { coverageCutoffLine, earningsLine, liquidityLine } from "./ratingRules";

type Camels = RoundReport["scorecard"]["camels"];

/** Ring and pip colours by rating 1–5; the number itself is always printed, so colour is never the only signal. */
const RATING_COLOR: Readonly<Record<number, string>> = {
  1: "var(--good)", 2: "var(--good)", 3: "var(--warn)", 4: "var(--crit)", 5: "var(--crit)",
};
const COMPONENT_ORDER = ["C", "A", "M", "E", "L", "S"] as const;
/** What each letter stands for, said on hover; the letters alone are all the tile has room for. */
const COMPONENT_NAME: Readonly<Record<(typeof COMPONENT_ORDER)[number], string>> = { C: "Capital", A: "Asset quality", M: "Management", E: "Earnings", L: "Liquidity", S: "Sensitivity to market risk" };

export function GxSupervisoryRating({ camels, status, report }: { readonly camels: Camels; readonly status: keyof typeof SUPERVISORY_WORDS; /** The year the rating closed: its quarters give the trailing return the earnings rule reads. */ readonly report?: RoundReport }) {
  const rules = usePreviewContext()?.params.supervisory;
  const rating = [report === undefined ? null : earningsLine(report, rules?.earningsRule), liquidityLine(camels, rules?.liquidityAfterRun), coverageCutoffLine(camels, rules)].filter((l) => l !== null);
  const composite = camels.composite;
  const ringColor = RATING_COLOR[composite] ?? "var(--good)";
  return (
    <div data-testid="cockpit.supervisory" className="gx-sup">
      <div className="gx-gate">
        <div className="gx-gate-ring" style={{ borderColor: ringColor }} aria-hidden="true">{composite}</div>
        <div>
          <div className="gx-gate-t">Composite {composite}{RATING_WORDS[composite] === undefined ? "" : ` · ${RATING_WORDS[composite]}`}</div>
          <div className="gx-gate-d">{SUPERVISORY_WORDS[status]} · {supervisoryTiming(composite, status)}</div>
        </div>
      </div>
      <table className="gx-table gx-sup-table">
        <thead><tr>{COMPONENT_ORDER.map((letter) => <th key={letter} scope="col">{COMPONENT_NAME[letter].replace(" to market risk", "")}</th>)}</tr></thead>
        <tbody><tr>{COMPONENT_ORDER.map((letter) => {
          const v = camels.components[letter];
          return <td key={letter} data-testid={`cockpit.supervisory.${letter}`} title={`${COMPONENT_NAME[letter]}: ${v} of 5`} ><GxPill tone={v <= 2 ? "good" : v === 3 ? "warn" : "crit"}>{v}</GxPill></td>;
        })}</tr></tbody>
      </table>
      <p className="gx-small gx-muted gx-sup-scale" data-testid="cockpit.supervisory.scale">Ratings run from 1 (best) to 5 (weakest).</p>
      {rating.length === 0 ? null : <ul className="gx-gate-rules" data-testid="cockpit.supervisory.rules">{rating.map((l) => <li key={l}>{l}</li>)}</ul>}
    </div>
  );
}
